import { CaretDown, CaretUp, CaretUpDown } from "@phosphor-icons/react";
import type { Table as ReactTable, Row } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";

import { tw } from "@/utils";
import { TableLoader } from "./TableLoader";

interface TableProps<T> {
  table: ReactTable<T>;
  handleRowClick?: (row: T, index: number) => void;
  isLoading?: boolean;
  className?: string;
  rowClassName?: (row: T) => string | undefined;
}

export const Table = <T,>({
  table,
  handleRowClick,
  isLoading,
  className,
  rowClassName,
}: TableProps<T>) => {
  if (isLoading) {
    return <TableLoader />;
  }

  return (
    <div
      className={tw(
        "overflow-x-auto rounded-t-2xl border border-neutral-200",
        className,
      )}
    >
      <table className="w-full">
        <thead className="w-full bg-primary-50 text-xs font-bold uppercase tracking-wide text-primary-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="w-full">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={tw(
                    "sticky top-0 flex-grow border-b border-neutral-200 bg-primary-50 px-4 py-3 text-left sm:px-6",
                    header.column.getCanSort() && "cursor-pointer select-none",
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder ? null : (
                    <div className="flex items-center gap-1">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {header.column.getCanSort() && (
                        <span className="ml-1 opacity-80">
                          {header.column.getIsSorted() === "asc" ? (
                            <CaretUp size={13} weight="bold" />
                          ) : header.column.getIsSorted() === "desc" ? (
                            <CaretDown size={13} weight="bold" />
                          ) : (
                            <CaretUpDown size={13} className="opacity-50" />
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td
                colSpan={table.getVisibleFlatColumns().length}
                className="px-6 py-16 text-center text-sm text-neutral-500"
              >
                No results found
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row: Row<T>) => (
              <tr
                key={row.id}
                className={tw(
                  "w-full border-b border-neutral-100 bg-white transition-colors hover:bg-primary-50/60",
                  handleRowClick && "cursor-pointer",
                  rowClassName?.(row.original),
                )}
                onClick={
                  handleRowClick
                    ? () => handleRowClick(row.original, row.index)
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="flex-grow px-4 py-2.5 sm:px-6">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
